import { useState, useEffect, useMemo } from 'react'
import { api } from '../../services/api'
import type { ClientServiceFormData, ServicePackage } from '../../services/api'
import ConfirmModal from '../ui/ConfirmModal'

interface Service {
  id: string
  name: string
  category: string
  base_price: number
  pricing_type: string
  description?: string
}

interface ClientService {
  id: string
  clientId: string
  serviceId: string
  service?: {
    id: string
    name: string
    category: string
    pricingType: string
    basePrice: number
    costPrice?: number | null
    vendor?: string
  }
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  startDate: string
  endDate: string | null
  executionDate: string | null
  quantity: number
  unitPrice: number | null
  actualCost?: number | null
  discountPercent: number
  billingFrequency: 'one_time' | 'monthly' | 'quarterly' | 'annual'
  estimatedHours: number | null
  actualHours: number | null
  deliverables: string[]
  notes: string | null
  effectivePrice?: number
  totalPrice?: number
  // Margin fields from backend (Sprint 13).
  effectiveCost?: number
  totalCost?: number
  margin?: number
  marginPct?: number | null
  // Sprint 7: traceability when this row came from applying a package.
  fromPackageId?: string | null
  fromPackage?: { id: string; name: string; slug: string } | null
}

interface ClientServicesProps {
  clientId: string
  clientName: string
  onClose: () => void
}

const STATUS_LABELS = {
  pending: 'Pendiente',
  in_progress: 'En Progreso',
  completed: 'Completado',
  cancelled: 'Cancelado'
}

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  in_progress: 'bg-blue-500/20 text-blue-400',
  completed: 'bg-green-500/20 text-green-400',
  cancelled: 'bg-red-500/20 text-red-400'
}

const BILLING_LABELS = {
  one_time: 'Unico',
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  annual: 'Anual'
}

const CATEGORY_LABELS: Record<string, string> = {
  package: 'Paquete',
  implementation: 'Implementacion',
  consulting: 'Consultoria',
  offensive: 'Seguridad Ofensiva',
  license: 'Licencia',
  automation: 'Automatizacion'
}

export default function ClientServices({ clientId, clientName, onClose }: ClientServicesProps) {
  const [clientServices, setClientServices] = useState<ClientService[]>([])
  const [availableServices, setAvailableServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [editingService, setEditingService] = useState<ClientService | null>(null)

  // Bulk selection (for "marcar N como completados" etc.)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkStatus, setBulkStatus] = useState<string>('')
  const [bulkBusy, setBulkBusy] = useState(false)

  // Delete via modal (replaces native confirm()).
  const [deleteTarget, setDeleteTarget] = useState<ClientService | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  // Timeline drawer
  const [timelineOpen, setTimelineOpen] = useState<ClientService | null>(null)

  // Sprint 7: package application flow
  const [packages, setPackages] = useState<ServicePackage[]>([])
  const [showPackageModal, setShowPackageModal] = useState(false)

  const fetchClientServices = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load packages in parallel — they're the same for every client
      // and only the active ones matter here (we're offering bundles to
      // assign, not managing the catalog).
      const [clientResult, servicesResult, packagesResult] = await Promise.all([
        api.listClientServices(clientId),
        api.listServices({ active: true }),
        api.listPackages({ active: true }),
      ])

      if (!clientResult.success) {
        throw new Error(clientResult.error || 'Error al cargar servicios del cliente')
      }

      setClientServices(clientResult.data?.services || [])
      setAvailableServices(servicesResult.data?.services || [])
      if (packagesResult.success && packagesResult.data) {
        setPackages((packagesResult.data as { packages: ServicePackage[] }).packages || [])
      }
    } catch (err) {
      console.error('Error fetching client services:', err)
      setError(err instanceof Error ? err.message : 'Error al cargar servicios')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchClientServices()
  }, [clientId])

  const handleAssignService = async (data: ClientServiceFormData) => {
    try {
      const result = await api.addClientService(clientId, data)

      if (!result.success) {
        throw new Error(result.error || 'Error al asignar servicio')
      }

      await fetchClientServices()
      setShowAssignModal(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al asignar')
    }
  }

  const handleUpdateService = async (id: string, data: Partial<ClientServiceFormData>) => {
    try {
      const result = await api.updateClientService(id, data)

      if (!result.success) {
        throw new Error(result.error || 'Error al actualizar servicio')
      }

      await fetchClientServices()
      setEditingService(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar')
    }
  }

  const confirmRemoveService = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      const result = await api.removeClientService(deleteTarget.id)
      if (!result.success) {
        throw new Error(result.error || 'Error al eliminar servicio')
      }
      await fetchClientServices()
      setDeleteTarget(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    } finally {
      setDeleteBusy(false)
    }
  }

  // Bulk status apply — wrapped around the new worker endpoint.
  const applyBulkStatus = async () => {
    if (selectedIds.size === 0 || !bulkStatus) return
    setBulkBusy(true)
    try {
      const ids = Array.from(selectedIds)
      const result = await api.bulkUpdateClientServices(clientId, ids, bulkStatus)
      if (!result.success) throw new Error(result.error || 'Error en bulk update')
      await fetchClientServices()
      setSelectedIds(new Set())
      setBulkStatus('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error en bulk update')
    } finally {
      setBulkBusy(false)
    }
  }

  // CSV export of the current filtered list. Escapes quotes so Excel
  // doesn't choke on service names with commas.
  const csvEscape = (v: unknown): string => {
    if (v === null || v === undefined) return ''
    const s = String(v)
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`
    }
    return s
  }

  const exportCSV = () => {
    const headers = [
      'Servicio', 'Categoría', 'Estado', 'Inicio', 'Fin', 'Ejecución',
      'Cantidad', 'Precio unitario', 'Descuento %', 'Total', 'Costo', 'Margen', 'Margen %',
      'Frecuencia', 'Horas estimadas', 'Horas reales', 'Notas',
    ]
    const rows = clientServices.map(cs => [
      cs.service?.name || '',
      cs.service?.category || '',
      STATUS_LABELS[cs.status] || cs.status,
      cs.startDate,
      cs.endDate || '',
      cs.executionDate || '',
      cs.quantity,
      cs.unitPrice ?? cs.effectivePrice ?? cs.service?.basePrice ?? '',
      cs.discountPercent,
      cs.totalPrice ?? '',
      cs.totalCost ?? '',
      cs.margin ?? '',
      cs.marginPct ?? '',
      BILLING_LABELS[cs.billingFrequency] || cs.billingFrequency,
      cs.estimatedHours ?? '',
      cs.actualHours ?? '',
      cs.notes || '',
    ])
    const csv = [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }) // BOM for Excel
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `servicios-${clientName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Select-all helper — mapped over current services each render.
  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(clientServices.map(s => s.id)) : new Set())
  }
  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const allSelected = useMemo(() =>
    clientServices.length > 0 && selectedIds.size === clientServices.length,
    [clientServices, selectedIds],
  )

  const calculateTotal = (service: ClientService) => {
    // Use effectivePrice from API, or calculate from unitPrice/basePrice
    if (service.totalPrice) return service.totalPrice
    const price = service.unitPrice || service.effectivePrice || service.service?.basePrice || 0
    const quantity = service.quantity || 1
    const discount = service.discountPercent || 0
    return price * quantity * (1 - discount / 100)
  }

  const activeServices = clientServices.filter(s => ['pending', 'in_progress'].includes(s.status))
  const totalActiveValue = activeServices.reduce((sum, s) => sum + calculateTotal(s), 0)
  const completedServices = clientServices.filter(s => s.status === 'completed')
  const totalCompletedValue = completedServices.reduce((sum, s) => sum + calculateTotal(s), 0)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-700">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Servicios de {clientName}</h2>
            <p className="text-gray-300 text-sm mt-1">
              {activeServices.length} servicios activos
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-severity-critical/20 border border-severity-critical text-severity-critical px-4 py-3 rounded-lg mb-6">
              {error}
              <button onClick={() => setError(null)} className="float-right">&times;</button>
            </div>
          )}

          {/* Revenue Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-gray-300 text-sm">Servicios Activos</p>
              <p className="text-2xl font-bold text-accent">
                ${totalActiveValue.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-gray-300 text-sm">Completados</p>
              <p className="text-2xl font-bold text-severity-success">
                ${totalCompletedValue.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-gray-300 text-sm">Total Servicios</p>
              <p className="text-2xl font-bold text-white">{clientServices.length}</p>
            </div>
          </div>

          {/* Toolbar: bulk select · export · add */}
          <div className="flex flex-wrap gap-2 items-center mb-4">
            {clientServices.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-gray-400 px-2 py-1 rounded hover:bg-gray-700/60 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={e => toggleAll(e.target.checked)}
                  className="w-4 h-4 accent-accent"
                />
                {selectedIds.size > 0 ? `${selectedIds.size} seleccionado(s)` : 'Seleccionar todos'}
              </label>
            )}
            {selectedIds.size > 0 && (
              <>
                <select
                  value={bulkStatus}
                  onChange={e => setBulkStatus(e.target.value)}
                  className="px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="">Cambiar estado a…</option>
                  <option value="pending">Pendiente</option>
                  <option value="in_progress">En progreso</option>
                  <option value="completed">Completado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
                <button
                  disabled={!bulkStatus || bulkBusy}
                  onClick={applyBulkStatus}
                  className="px-3 py-1.5 text-xs rounded bg-accent hover:bg-accent-hover text-white font-medium disabled:opacity-50 flex items-center gap-1"
                >
                  {bulkBusy && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />}
                  Aplicar
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-gray-400 hover:text-white underline"
                >
                  Deseleccionar
                </button>
              </>
            )}
            {clientServices.length > 0 && (
              <button
                onClick={exportCSV}
                className="ml-auto px-3 py-1.5 text-xs rounded border border-gray-600 text-gray-300 hover:bg-gray-700 flex items-center gap-1"
                title="Exportar vista actual a CSV"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Exportar CSV
              </button>
            )}
            {/* Sprint 7: apply a package (expands into N services).
                Only visible when there's at least one active package
                in the catalog — keeps the toolbar quiet otherwise. */}
            {packages.length > 0 && (
              <button
                onClick={() => setShowPackageModal(true)}
                className={`${clientServices.length > 0 ? '' : 'ml-auto'} bg-purple-500/90 hover:bg-purple-500 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2`}
                title="Aplicar un paquete (Shield, Advance, etc.)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Aplicar paquete
              </button>
            )}
            <button
              onClick={() => setShowAssignModal(true)}
              className={`${clientServices.length > 0 || packages.length > 0 ? '' : 'ml-auto'} bg-accent hover:bg-accent-hover text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Agregar Servicio
            </button>
          </div>

          {/* Services List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-gray-300">Cargando servicios...</p>
            </div>
          ) : clientServices.length === 0 ? (
            <div className="text-center py-12 bg-gray-700 rounded-xl">
              <svg className="w-12 h-12 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p className="text-gray-300 mb-4">Este cliente no tiene servicios asignados</p>
              <button
                onClick={() => setShowAssignModal(true)}
                className="text-accent hover:underline"
              >
                Asignar primer servicio
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {clientServices.map((cs) => (
                <div
                  key={cs.id}
                  className={`bg-gray-700 rounded-lg p-4 flex items-center justify-between ${
                    selectedIds.has(cs.id) ? 'ring-2 ring-accent/60' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(cs.id)}
                    onChange={() => toggleOne(cs.id)}
                    className="mr-4 w-4 h-4 accent-accent flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-white font-medium">{cs.service?.name || 'Servicio sin nombre'}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[cs.status]}`}>
                        {STATUS_LABELS[cs.status]}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                        {BILLING_LABELS[cs.billingFrequency]}
                      </span>
                      {/* Sprint 7: package provenance badge. Highlights
                          rows that came from applying a bundle so the
                          admin sees the grouping at a glance. */}
                      {cs.fromPackage && (
                        <span
                          className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-300 border border-orange-500/40 flex items-center gap-1"
                          title={`Parte del paquete ${cs.fromPackage.name}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                          {cs.fromPackage.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-300">
                      <span>{CATEGORY_LABELS[cs.service?.category || ''] || cs.service?.category || 'Sin categoría'}</span>
                      {cs.quantity > 1 && <span>Cantidad: {cs.quantity}</span>}
                      {cs.discountPercent > 0 && (
                        <span className="text-severity-success">-{cs.discountPercent}% desc</span>
                      )}
                    </div>
                    {cs.notes && (
                      <p className="text-gray-500 text-sm mt-1">{cs.notes}</p>
                    )}
                    <div className="flex gap-4 text-xs text-gray-500 mt-2">
                      <span>Inicio: {new Date(cs.startDate).toLocaleDateString('es-CL')}</span>
                      {cs.endDate && (
                        <span>Fin: {new Date(cs.endDate).toLocaleDateString('es-CL')}</span>
                      )}
                      {cs.executionDate && (
                        <span className="text-severity-warning font-medium">
                          Ejecucion: {new Date(cs.executionDate).toLocaleDateString('es-CL', { year: 'numeric', month: 'long' })}
                        </span>
                      )}
                    </div>
                    {(cs.estimatedHours || cs.actualHours) && (
                      <div className="flex gap-4 text-xs text-gray-500 mt-1">
                        {cs.estimatedHours && <span>Estimado: {cs.estimatedHours}h</span>}
                        {cs.actualHours && <span>Real: {cs.actualHours}h</span>}
                      </div>
                    )}
                  </div>

                  <div className="text-right mr-4">
                    <p className="text-accent font-semibold">
                      ${calculateTotal(cs).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {cs.unitPrice || cs.effectivePrice ? `$${cs.unitPrice || cs.effectivePrice} x ${cs.quantity}` : '-'}
                    </p>
                    {/* Margin info — shows only when we have cost data.
                        Green = healthy (>= 30%), yellow = watch, red = loss. */}
                    {cs.margin !== undefined && cs.totalCost !== undefined && cs.totalCost > 0 && (
                      <p
                        className={`text-xs mt-1 ${
                          cs.marginPct === null ? 'text-gray-500'
                            : (cs.marginPct ?? 0) >= 30 ? 'text-green-400'
                            : (cs.marginPct ?? 0) >= 10 ? 'text-yellow-400'
                            : 'text-red-400'
                        }`}
                        title={`Costo total: $${(cs.totalCost || 0).toLocaleString()}`}
                      >
                        Margen ${Math.round(cs.margin).toLocaleString()}
                        {cs.marginPct !== null && cs.marginPct !== undefined ? ` (${cs.marginPct.toFixed(1)}%)` : ''}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-1">
                    <button
                      onClick={() => setTimelineOpen(cs)}
                      className="text-gray-300 hover:text-blue-400 p-2 rounded hover:bg-gray-600"
                      title="Ver timeline"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setEditingService(cs)}
                      className="text-gray-300 hover:text-accent p-2 rounded hover:bg-gray-600"
                      title="Editar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setDeleteTarget(cs)}
                      className="text-gray-300 hover:text-red-400 p-2 rounded hover:bg-gray-600"
                      title="Eliminar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Assign Service Modal */}
      {showAssignModal && (
        <AssignServiceModal
          services={availableServices}
          existingServiceIds={clientServices.map(cs => cs.serviceId)}
          onSave={handleAssignService}
          onClose={() => setShowAssignModal(false)}
        />
      )}

      {/* Edit Service Modal */}
      {editingService && (
        <EditClientServiceModal
          clientService={editingService}
          onSave={(data) => handleUpdateService(editingService.id, data)}
          onClose={() => setEditingService(null)}
        />
      )}

      {/* Delete confirm (replaces native confirm()) */}
      {deleteTarget && (
        <ConfirmModal
          open={true}
          title={`¿Eliminar "${deleteTarget.service?.name || 'servicio'}"?`}
          message={
            ['pending', 'cancelled'].includes(deleteTarget.status)
              ? 'El servicio se enviará a la papelera. Si fue asignado por error, podés restaurarlo al borrar el cliente.'
              : `Este servicio está en estado "${STATUS_LABELS[deleteTarget.status]}". Sólo se pueden eliminar servicios pendientes o cancelados. Cambialo de estado primero.`
          }
          detail={
            ['pending', 'cancelled'].includes(deleteTarget.status)
              ? `Total: $${calculateTotal(deleteTarget).toLocaleString()}`
              : null
          }
          variant="danger"
          confirmLabel={['pending', 'cancelled'].includes(deleteTarget.status) ? 'Eliminar' : 'Entendido'}
          busy={deleteBusy}
          onConfirm={
            ['pending', 'cancelled'].includes(deleteTarget.status)
              ? confirmRemoveService
              : () => setDeleteTarget(null)
          }
          onCancel={() => !deleteBusy && setDeleteTarget(null)}
        />
      )}

      {/* Timeline drawer */}
      {timelineOpen && (
        <TimelineDrawer
          service={timelineOpen}
          onClose={() => setTimelineOpen(null)}
        />
      )}

      {/* Sprint 7: apply a package (expands into N client_services). */}
      {showPackageModal && (
        <ApplyPackageModal
          packages={packages}
          clientId={clientId}
          clientName={clientName}
          onClose={() => setShowPackageModal(false)}
          onApplied={async () => {
            setShowPackageModal(false)
            await fetchClientServices()
          }}
        />
      )}
    </div>
  )
}

// =====================================================================
// ApplyPackageModal — picker + form to apply one of the active
// packages to this client. The backend expands the package into N
// client_services rows and tags them with from_package_id.
// =====================================================================
function ApplyPackageModal({ packages, clientId, clientName, onClose, onApplied }: {
  packages: ServicePackage[]
  clientId: string
  clientName: string
  onClose: () => void
  onApplied: () => Promise<void>
}) {
  const [selected, setSelected] = useState<ServicePackage | null>(packages[0] || null)
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
  const [userCount, setUserCount] = useState(1)
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ created: number; skipped: number; skippedServices: string[] } | null>(null)

  const submit = async () => {
    if (!selected) return
    setError(null)
    setSubmitting(true)
    try {
      const res = await api.applyPackage(clientId, {
        packageId: selected.id,
        startDate,
        userCount: selected.priceModel === 'per_user' ? userCount : undefined,
        notes: notes.trim() || undefined,
      })
      if (!res.success || !res.data) throw new Error(res.error || 'Error al aplicar paquete')
      const d = res.data as { created: number; skipped: number; skippedServices: string[] }
      setResult(d)
      // Auto-close after success (unless there are skips to show).
      if (d.skipped === 0) {
        setTimeout(() => { onApplied() }, 800)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  // Sprint 7.1: resolve the tier that applies to this user count so
  // the preview reflects the same scaling factor the backend will use.
  // Keeps the preview honest — what you see in the modal matches
  // exactly what upsertInvoice will produce.
  const appliedTier = (() => {
    if (!selected || selected.priceModel !== 'per_user' || !selected.pricingTiers?.length) return null
    return selected.pricingTiers.find(t =>
      userCount >= t.min_users && (t.max_users === null || userCount <= t.max_users)
    ) || null
  })()

  // Pricing preview for the selected package. Uses the tier's
  // price_per_user when available; otherwise falls back to the
  // base sum of components.
  const totalPreview = (() => {
    if (!selected) return 0
    if (appliedTier && selected.priceModel === 'per_user') {
      return appliedTier.price_per_user * userCount
    }
    const multiplier = selected.priceModel === 'per_user' ? userCount : 1
    return selected.components.reduce((s, c) => {
      const unit = c.overridePrice ?? c.serviceBasePrice
      return s + unit * c.quantity * multiplier * (1 - (c.discountPercent || 0) / 100)
    }, 0)
  })()

  // Scaling factor to show per-component breakdown in the preview.
  const previewScaling = (() => {
    if (!selected || !appliedTier || selected.priceModel !== 'per_user') return 1
    const baseSum = selected.components.reduce((s, c) => {
      const unit = c.overridePrice ?? c.serviceBasePrice
      return s + unit * c.quantity
    }, 0)
    return baseSum > 0 ? appliedTier.price_per_user / baseSum : 1
  })()

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Aplicar paquete · {clientName}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              El paquete se expande en {selected?.components.length || 0} servicio(s) asignados a este cliente.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-300 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {result && (
            <div className={`px-3 py-2 rounded text-sm ${
              result.skipped > 0
                ? 'bg-yellow-500/20 border border-yellow-500 text-yellow-200'
                : 'bg-green-500/20 border border-green-500 text-green-200'
            }`}>
              <strong>✓ {result.created} servicio(s) creado(s).</strong>
              {result.skipped > 0 && (
                <div className="mt-1 text-xs">
                  Se saltaron {result.skipped} (ya existían activos): {result.skippedServices.join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Package picker */}
          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">Paquete a aplicar</label>
            <div className="grid gap-2">
              {packages.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelected(p)}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    selected?.id === p.id
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-gray-700 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{p.name}</span>
                        <span className="text-xs text-gray-500 font-mono">{p.slug}</span>
                      </div>
                      {p.description && <p className="text-gray-400 text-sm mt-0.5">{p.description}</p>}
                      <p className="text-xs text-gray-500 mt-1">
                        {p.components.length} componente(s) ·
                        {p.priceModel === 'per_user' ? ' Por usuario/mes' : p.priceModel === 'fixed_monthly' ? ' Fijo mensual' : ' Custom'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-green-400 font-bold">${p.displayPrice}</div>
                      <div className="text-xs text-gray-500">{p.currency}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Apply params */}
          {selected && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Fecha de inicio</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-purple-500"
                />
              </div>
              {selected.priceModel === 'per_user' && (
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">
                    Cantidad de usuarios
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={userCount}
                    onChange={e => setUserCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Multiplicador de cantidad por componente</p>
                </div>
              )}
            </div>
          )}

          {selected && (
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Notas (opcional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                placeholder="Ej: aplicado según contrato firmado 2026-04-21"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-purple-500"
              />
            </div>
          )}

          {/* Preview */}
          {selected && selected.components.length > 0 && (
            <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400 uppercase tracking-wider">Vista previa</span>
                <span className="text-xl font-bold text-green-400">
                  ${totalPreview.toFixed(2)}/mes
                </span>
              </div>

              {/* Sprint 7.1: tier applied badge */}
              {appliedTier && (
                <div className="mb-2 text-xs bg-purple-500/10 border border-purple-500/30 rounded px-2 py-1 text-purple-200 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Tier aplicada: <strong>{appliedTier.min_users}-{appliedTier.max_users ?? '∞'} usuarios</strong>
                  → ${appliedTier.price_per_user}/user ·
                  factor {previewScaling.toFixed(3)}×
                </div>
              )}

              <div className="space-y-1 text-sm">
                {selected.components.map(c => {
                  const baseUnit = c.overridePrice ?? c.serviceBasePrice
                  // Scale unit price by tier factor when applicable.
                  const unit = Math.round(baseUnit * previewScaling * 100) / 100
                  const qty = c.quantity * (selected.priceModel === 'per_user' ? userCount : 1)
                  const line = unit * qty * (1 - (c.discountPercent || 0) / 100)
                  return (
                    <div key={c.id} className="flex items-center gap-2 text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                      <span className="flex-1">{c.serviceName}</span>
                      <span className="text-gray-500 text-xs font-mono">
                        {qty} × ${unit}
                        {previewScaling !== 1 && (
                          <span className="text-orange-400 ml-1" title={`Base: $${baseUnit}`}>
                            (tier)
                          </span>
                        )}
                      </span>
                      <span className="text-white font-semibold w-20 text-right font-mono">
                        ${line.toFixed(2)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting}
                  className="px-3 py-1.5 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50">
            {result ? 'Cerrar' : 'Cancelar'}
          </button>
          {!result && (
            <button
              onClick={submit}
              disabled={!selected || submitting || (selected && selected.components.length === 0)}
              className="px-4 py-1.5 text-sm rounded bg-purple-500 hover:bg-purple-600 text-white font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />}
              Aplicar paquete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// =====================================================================
// TimelineDrawer — sliding panel that shows every event logged for a
// client service (status changes, notes, milestones, email notifications).
// Lets the operator also append a free-form note without leaving the page.
// =====================================================================
function TimelineDrawer({ service, onClose }: {
  service: ClientService
  onClose: () => void
}) {
  const [events, setEvents] = useState<Array<{
    id: string; type: string; fromStatus: string | null; toStatus: string | null;
    note: string | null; actor: { id: string; name: string | null; email: string | null } | null;
    createdAt: string;
  }>>([])
  const [loading, setLoading] = useState(true)
  const [noteText, setNoteText] = useState('')
  const [noteType, setNoteType] = useState<'note' | 'milestone' | 'contact'>('note')
  const [posting, setPosting] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.listClientServiceEvents(service.id)
      if (res.success && res.data) {
        setEvents((res.data as unknown as { events: typeof events }).events || [])
      }
    } finally { setLoading(false) }
  }
  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [service.id])

  const submit = async () => {
    if (!noteText.trim()) return
    setPosting(true)
    try {
      await api.addClientServiceEvent(service.id, { type: noteType, note: noteText.trim() })
      setNoteText('')
      await load()
    } finally { setPosting(false) }
  }

  const EVENT_META: Record<string, { label: string; color: string; icon: string }> = {
    status_change: { label: 'Cambio de estado', color: 'bg-blue-500/20 text-blue-300', icon: '→' },
    note:          { label: 'Nota interna',      color: 'bg-gray-600/40 text-gray-200', icon: '📝' },
    milestone:     { label: 'Hito',              color: 'bg-purple-500/20 text-purple-300', icon: '🏁' },
    contact:       { label: 'Contacto cliente',  color: 'bg-yellow-500/20 text-yellow-300', icon: '📞' },
    email_sent:    { label: 'Email enviado',     color: 'bg-green-500/20 text-green-300', icon: '✉' },
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-md bg-gray-800 border-l border-gray-700 h-full flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-700 flex items-start justify-between">
          <div>
            <h3 className="text-white font-semibold">Timeline</h3>
            <p className="text-gray-400 text-sm">{service.service?.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* New note input */}
        <div className="p-4 border-b border-gray-700 bg-gray-900/40">
          <div className="flex gap-2 mb-2">
            {(['note', 'milestone', 'contact'] as const).map(t => (
              <button
                key={t}
                onClick={() => setNoteType(t)}
                className={`px-2 py-1 text-xs rounded ${
                  noteType === t ? 'bg-accent text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >{EVENT_META[t].label}</button>
            ))}
          </div>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={2}
            placeholder="Agregar nota / hito / contacto…"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <button
            onClick={submit}
            disabled={!noteText.trim() || posting}
            className="mt-2 w-full px-3 py-1.5 text-sm rounded bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-medium"
          >
            {posting ? 'Guardando…' : 'Agregar al timeline'}
          </button>
        </div>

        {/* Events list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-gray-400 text-sm text-center py-8">Cargando…</p>
          ) : events.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">Sin eventos todavía.</p>
          ) : (
            <ol className="space-y-3">
              {events.map(e => {
                const meta = EVENT_META[e.type] || { label: e.type, color: 'bg-gray-600/40 text-gray-300', icon: '•' }
                return (
                  <li key={e.id} className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs ${meta.color}`}>
                        {meta.icon} {meta.label}
                      </span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {new Date(e.createdAt).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </div>
                    {e.type === 'status_change' && (
                      <p className="text-sm text-gray-200">
                        <span className="text-gray-500">{e.fromStatus || '—'}</span>
                        {' → '}
                        <span className="text-white font-medium">{e.toStatus}</span>
                      </p>
                    )}
                    {e.note && (
                      <p className="text-sm text-gray-300 mt-1 whitespace-pre-wrap">{e.note}</p>
                    )}
                    {e.actor && (
                      <p className="text-xs text-gray-500 mt-1">— {e.actor.name || e.actor.email}</p>
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  )
}

const VENDOR_OPTIONS = [
  { value: 'cloudflare', label: 'Cloudflare' },
  { value: 'aws', label: 'AWS' },
  { value: 'azure', label: 'Azure' },
  { value: 'crowdstrike', label: 'CrowdStrike' },
  { value: 'jumpcloud', label: 'JumpCloud' },
  { value: 'jarix', label: 'Jarix (Manual)' },
  { value: 'other', label: 'Otro' }
]

// Assign Service Modal
interface AssignServiceModalProps {
  services: Service[]
  existingServiceIds: string[]
  onSave: (data: ClientServiceFormData) => void
  onClose: () => void
  onServiceCreated?: (service: Service) => void
}

function AssignServiceModal({ services, existingServiceIds, onSave, onClose, onServiceCreated }: AssignServiceModalProps) {
  const [mode, setMode] = useState<'select' | 'create'>('select')
  const [selectedService, setSelectedService] = useState<Service | null>(null)
  const [creatingService, setCreatingService] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Form for assigning service
  const [formData, setFormData] = useState({
    status: 'pending' as ClientService['status'],
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    executionDate: '',
    quantity: 1,
    unitPrice: 0,
    discountPercent: 0,
    billingFrequency: 'one_time' as ClientService['billingFrequency'],
    estimatedHours: '',
    notes: ''
  })

  // Form for creating new service
  const [newServiceData, setNewServiceData] = useState({
    name: '',
    category: 'consulting',
    vendor: 'jarix',
    basePrice: 0,
    pricingType: 'one_time'
  })

  const availableToAssign = services.filter(s => !existingServiceIds.includes(s.id))

  const handleSelectService = (service: Service) => {
    setSelectedService(service)
    setFormData(prev => ({
      ...prev,
      unitPrice: service.base_price
    }))
  }

  const handleCreateService = async () => {
    if (!newServiceData.name.trim()) {
      setCreateError('El nombre del servicio es requerido')
      return
    }

    setCreatingService(true)
    setCreateError(null)

    try {
      const result = await api.createService({
        name: newServiceData.name,
        category: newServiceData.category as 'package' | 'implementation' | 'consulting' | 'offensive' | 'license' | 'automation',
        vendor: newServiceData.vendor,
        base_price: newServiceData.basePrice,
        pricing_type: newServiceData.pricingType as 'per_user_monthly' | 'one_time' | 'hourly' | 'project' | 'license_monthly' | 'license_annual',
        is_active: 1
      })

      if (!result.success) {
        throw new Error(result.error || 'Error al crear servicio')
      }

      // Create a service object with the new ID
      const createdService: Service = {
        id: result.data?.id || '',
        name: newServiceData.name,
        category: newServiceData.category as 'package' | 'implementation' | 'consulting' | 'offensive' | 'license' | 'automation',
        base_price: newServiceData.basePrice,
        pricing_type: newServiceData.pricingType as 'per_user_monthly' | 'one_time' | 'hourly' | 'project' | 'license_monthly' | 'license_annual'
      }

      // Select the new service and switch to assignment
      setSelectedService(createdService)
      setFormData(prev => ({
        ...prev,
        unitPrice: createdService.base_price
      }))
      setMode('select')

      // Notify parent to refresh service list
      if (onServiceCreated) {
        onServiceCreated(createdService)
      }
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear servicio')
    } finally {
      setCreatingService(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedService) return

    onSave({
      serviceId: selectedService.id,
      status: formData.status,
      startDate: formData.startDate,
      endDate: formData.endDate || null,
      executionDate: formData.executionDate || null,
      quantity: formData.quantity,
      unitPrice: formData.unitPrice || null,
      discountPercent: formData.discountPercent,
      billingFrequency: formData.billingFrequency,
      estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null,
      notes: formData.notes || null
    })
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Agregar Servicio</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Mode Toggle */}
          <div className="flex gap-2 p-1 bg-gray-700 rounded-lg">
            <button
              type="button"
              onClick={() => setMode('select')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'select'
                  ? 'bg-accent text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Seleccionar Existente
            </button>
            <button
              type="button"
              onClick={() => setMode('create')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'create'
                  ? 'bg-accent text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Crear Nuevo
            </button>
          </div>

          {mode === 'create' ? (
            /* Create New Service Form */
            <div className="space-y-4 p-4 bg-gray-700 rounded-lg">
              {createError && (
                <div className="bg-severity-critical/20 border border-severity-critical text-severity-critical px-3 py-2 rounded text-sm">
                  {createError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nombre del Servicio *
                </label>
                <input
                  type="text"
                  value={newServiceData.name}
                  onChange={(e) => setNewServiceData({ ...newServiceData, name: e.target.value })}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Ej: WAF Cloudflare, Alineacion PCI..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Categoria
                  </label>
                  <select
                    value={newServiceData.category}
                    onChange={(e) => setNewServiceData({ ...newServiceData, category: e.target.value })}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Proveedor
                  </label>
                  <select
                    value={newServiceData.vendor}
                    onChange={(e) => setNewServiceData({ ...newServiceData, vendor: e.target.value })}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {VENDOR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Precio Base (USD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newServiceData.basePrice}
                    onChange={(e) => setNewServiceData({ ...newServiceData, basePrice: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Tipo de Precio
                  </label>
                  <select
                    value={newServiceData.pricingType}
                    onChange={(e) => setNewServiceData({ ...newServiceData, pricingType: e.target.value })}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="one_time">Pago Unico</option>
                    <option value="project">Por Proyecto</option>
                    <option value="hourly">Por Hora</option>
                    <option value="per_user_monthly">Por Usuario/Mes</option>
                    <option value="license_monthly">Licencia Mensual</option>
                    <option value="license_annual">Licencia Anual</option>
                  </select>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCreateService}
                disabled={creatingService || !newServiceData.name.trim()}
                className="w-full bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {creatingService ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Crear y Seleccionar
                  </>
                )}
              </button>
            </div>
          ) : (
            /* Select Existing Service */
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Seleccionar Servicio *
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableToAssign.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm mb-2">
                      No hay servicios disponibles para asignar
                    </p>
                    <button
                      type="button"
                      onClick={() => setMode('create')}
                      className="text-accent hover:underline text-sm"
                    >
                      Crear nuevo servicio
                    </button>
                  </div>
                ) : (
                  availableToAssign.map((service) => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => handleSelectService(service)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedService?.id === service.id
                          ? 'border-accent bg-accent/10'
                          : 'border-gray-700 hover:border-content-tertiary'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">{service.name}</p>
                          <p className="text-gray-300 text-sm">
                            {CATEGORY_LABELS[service.category] || service.category}
                          </p>
                        </div>
                        <p className="text-accent font-semibold">${service.base_price}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {selectedService && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Estado
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as ClientService['status'] })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Frecuencia de Facturacion
                  </label>
                  <select
                    value={formData.billingFrequency}
                    onChange={(e) => setFormData({ ...formData, billingFrequency: e.target.value as ClientService['billingFrequency'] })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {Object.entries(BILLING_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Horas Estimadas
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Ej: 40"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Precio Unitario
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unitPrice}
                    onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Descuento (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discountPercent}
                    onChange={(e) => setFormData({ ...formData, discountPercent: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Fecha Inicio (Contrato) *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              </div>

              {/* Execution Date - Critical for billing */}
              {formData.billingFrequency === 'one_time' && (
                <div className="bg-severity-warning/10 border border-severity-warning/30 rounded-lg p-4">
                  <label className="block text-sm font-medium text-severity-warning mb-1">
                    Mes de Ejecucion (Facturacion) *
                  </label>
                  <input
                    type="month"
                    value={formData.executionDate ? formData.executionDate.substring(0, 7) : ''}
                    onChange={(e) => setFormData({ ...formData, executionDate: e.target.value + '-01' })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Este servicio se facturara en el mes seleccionado
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notas
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                  rows={2}
                  placeholder="Notas sobre el servicio..."
                />
              </div>

              {/* Total Preview */}
              <div className="bg-gray-600 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Subtotal:</span>
                  <span className="text-white">${(formData.unitPrice * formData.quantity).toFixed(2)}</span>
                </div>
                {formData.discountPercent > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">Descuento ({formData.discountPercent}%):</span>
                    <span className="text-severity-success">
                      -${(formData.unitPrice * formData.quantity * formData.discountPercent / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold mt-2 pt-2 border-t border-gray-700">
                  <span className="text-gray-300">Total:</span>
                  <span className="text-accent">
                    ${(formData.unitPrice * formData.quantity * (1 - formData.discountPercent / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!selectedService}
              className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Agregar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Client Service Modal
interface EditClientServiceModalProps {
  clientService: ClientService
  onSave: (data: Partial<ClientServiceFormData>) => void
  onClose: () => void
}

function EditClientServiceModal({ clientService, onSave, onClose }: EditClientServiceModalProps) {
  const [formData, setFormData] = useState({
    status: clientService.status,
    startDate: clientService.startDate,
    endDate: clientService.endDate || '',
    executionDate: clientService.executionDate || '',
    quantity: clientService.quantity,
    unitPrice: clientService.unitPrice || clientService.effectivePrice || clientService.service?.basePrice || 0,
    actualCost: clientService.actualCost ?? clientService.service?.costPrice ?? 0,
    discountPercent: clientService.discountPercent,
    billingFrequency: clientService.billingFrequency,
    estimatedHours: clientService.estimatedHours?.toString() || '',
    actualHours: clientService.actualHours?.toString() || '',
    notes: clientService.notes || ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      status: formData.status,
      startDate: formData.startDate,
      endDate: formData.endDate || null,
      executionDate: formData.executionDate || null,
      quantity: formData.quantity,
      unitPrice: formData.unitPrice || null,
      actualCost: formData.actualCost ? formData.actualCost : null,
      discountPercent: formData.discountPercent,
      billingFrequency: formData.billingFrequency,
      estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null,
      actualHours: formData.actualHours ? parseFloat(formData.actualHours) : null,
      notes: formData.notes || null
    })
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Editar: {clientService.service?.name || 'Servicio'}</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as ClientService['status'] })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Frecuencia</label>
              <select
                value={formData.billingFrequency}
                onChange={(e) => setFormData({ ...formData, billingFrequency: e.target.value as ClientService['billingFrequency'] })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {Object.entries(BILLING_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Cantidad</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Precio Unitario</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.unitPrice}
                onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) || 0 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Costo real
                <span className="text-gray-500 font-normal ml-1">(opcional)</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.actualCost}
                onChange={(e) => setFormData({ ...formData, actualCost: parseFloat(e.target.value) || 0 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder={`Default: $${clientService.service?.costPrice || 0}`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Override del costo del catálogo. Útil cuando este engagement cuesta distinto al promedio.
                Define el margen real vs. revenue.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Descuento (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.discountPercent}
                onChange={(e) => setFormData({ ...formData, discountPercent: parseFloat(e.target.value) || 0 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Inicio</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Fin</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>

          {/* Execution Date - Critical for billing */}
          {formData.billingFrequency === 'one_time' && (
            <div className="bg-severity-warning/10 border border-severity-warning/30 rounded-lg p-4">
              <label className="block text-sm font-medium text-severity-warning mb-1">
                Mes de Ejecucion (Facturacion)
              </label>
              <input
                type="month"
                value={formData.executionDate ? formData.executionDate.substring(0, 7) : ''}
                onChange={(e) => setFormData({ ...formData, executionDate: e.target.value + '-01' })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Este servicio se facturara en el mes seleccionado
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Horas Estimadas</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={formData.estimatedHours}
                onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Horas Reales</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={formData.actualHours}
                onChange={(e) => setFormData({ ...formData, actualHours: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              rows={2}
            />
          </div>

          {/* Total Preview */}
          <div className="bg-gray-600 rounded-lg p-4">
            <div className="flex justify-between text-lg font-semibold">
              <span className="text-gray-300">Total:</span>
              <span className="text-accent">
                ${(formData.unitPrice * formData.quantity * (1 - formData.discountPercent / 100)).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors"
            >
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
